import React, { useContext, useState } from 'react';
import { Box, Button, Typography, TextField, ButtonBase, Card } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import SnackbarContext from '../contexts/SnackbarContext';

/* Icons */
import EmailIcon from '@mui/icons-material/Email';
import KeyIcon from '@mui/icons-material/Key';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import IconButton from '@mui/material/IconButton';
import { ArrowBackRounded } from '@mui/icons-material';
import { layoutButtonPillRadius, layoutCardRadius, layoutIconButtonRadius, layoutShopSheetRadius } from '../theme/colors';

/* Padrão Campos Registro */
const FieldLogin = ({ label, icon, type = 'text', value, onChange, placeholder, onKeyPress, name, autoComplete, showPasswordToggle = false, onTogglePassword, showPassword }) => {

  const getIcon = () => {
    if (icon) return icon;
    // Fallback para ícones padrão baseado no tipo
    if (type === 'email') return <EmailIcon sx={{ fontSize: 16 }} />;
    if (type === 'password') return <KeyIcon sx={{ fontSize: 16 }} />;
    return null;
  };

  return (
    /* Login/Email */
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1, alignSelf: 'stretch' }}>
      {/* Título Campo */}
      <Typography sx={{ color: '#000', fontFamily: 'Nunito', fontSize: '14px', fontWeight: 700, lineHeight: '14px' }}>
        {label}
      </Typography>

      {/* TextField */}
      <Box sx={{ position: 'relative', width: '100%' }}>
        <TextField
          placeholder={placeholder}
          name={name}
          variant="outlined"
          type={showPasswordToggle && showPassword ? 'text' : type}
          autoComplete={autoComplete}
          value={value}
          onChange={onChange}
          onKeyPress={onKeyPress}
          fullWidth
          InputProps={{
            sx: {
              /* Estilo do input */
              '& .MuiOutlinedInput-input': {
                paddingLeft: '45px !important',
                paddingRight: showPasswordToggle ? '45px !important' : '14px',
                zIndex: 1,
                bgcolor: 'surface.input', borderRadius: layoutCardRadius,

                /* Borda do autofill */
                '&:-webkit-autofill': {
                  borderRadius: `${layoutCardRadius} !important`,
                  zIndex: 1,
                },
              },

              /* Campo preenchido */
              '& .MuiOutlinedInput-notchedOutline': {
                borderWidth: '1px',
                borderRadius: `${layoutCardRadius} !important`,
                zIndex: 2,
              },
            }
          }}
        />

        {/* Icone */}
        <Box sx={{ /* zIndex = 10 alta prioridade */
          position: 'absolute',
          left: '16px',
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 10,
          display: 'flex',
          alignItems: 'center',
        }}>
          {getIcon()}
        </Box>

        {/* Botão de ver senha*/}
        {showPasswordToggle && (
          <IconButton
            onClick={onTogglePassword}
            sx={{
              position: 'absolute',
              right: '16px',
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 10,
              color: '#757575',
              padding: '4px',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              }
            }}
          >
            {showPassword ? <VisibilityOffIcon sx={{ fontSize: 16 }} /> : <VisibilityIcon sx={{ fontSize: 16 }} />}
          </IconButton>
        )}
      </Box>
    </Box>
  );
};

/* Padrão Campos Perfil */
const FormProfile = ({ label, children, sx = {} }) => (
  /* Info Perfil */
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1, alignSelf: 'stretch', ...sx }}>
    {/* Título Campo */}
    <Typography sx={{ color: '#000', fontFamily: 'Nunito', fontSize: '14px', fontWeight: 700, lineHeight: '14px' }}>
      {label}
    </Typography>

    {/* TextField // Autocomplete*/}
    {children}
  </Box>
);

const EditProfile = () => {
  const navigate = useNavigate();
  const { openSnackbar } = useContext(SnackbarContext);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  /*Mostrar/ocultar senha*/
  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };

  /*Mostrar/ocultar confirmar senha*/
  const handleToggleConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
  };

  const handleConfirmPasswordChange = (e) => {
    setConfirmPassword(e.target.value);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Verificar se as senhas são iguais
    if (password !== confirmPassword) {
      openSnackbar("As senhas estão diferentes", "error");
      return;
    }
  };

  // Função para cancelar
  const handleCancel = () => {
    openSnackbar("Alterações canceladas", "info");
    navigate(`/profile`);
  };

  const buttonStyle = {
    display: 'flex',
    width: '100%',
    p: '16px 0',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 2,
    borderRadius: layoutButtonPillRadius,
    fontFamily: 'Nunito',
    fontSize: '16px',
    fontWeight: 600,
    lineHeight: '24px',
    color: '#ffffff'
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', alignItems: 'center', gap: 10, bgcolor: 'surface.card', borderRadius: layoutShopSheetRadius, padding: '24px' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 3, alignSelf: 'stretch' }}>
        {/* Voltar e Título */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, alignSelf: 'stretch', position: 'relative' }}>
          <Button data-no-shadow="true" onClick={() => window.history.back()} sx={{ color: 'text.primary', borderRadius: layoutIconButtonRadius }}>
            <ArrowBackRounded />
          </Button>

          {/* Título */}
          <Typography variant="pageTitle" sx={{ width: 'auto', transform: 'translateX(-50%)', position: 'absolute', left: '50%' }}>Editar perfil</Typography>
        </Box>

        {/* Informações Principais*/}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, alignSelf: 'stretch' }}>
          {/* Nome do usuário */}
          <FormProfile label="Nome">
            <TextField
              //label="Nome"
              variant="outlined"
              //value={pet.name || ''}
              name="name"
              id="profile-name"
              placeholder="Digite seu nome"
              fullWidth
              //onChange={handleFormFieldChange}
              autoComplete="section-profile name"
              sx={{ bgcolor: 'surface.input', borderRadius: layoutCardRadius }}
            />
          </FormProfile>

          {/* Email do usuário */}
          <FormProfile label="Email">
            <TextField
              //label="Nome"
              variant="outlined"
              //value={pet.name || ''}
              name="email"
              id="username"
              type="email"
              placeholder="Digite seu email"
              fullWidth
              //onChange={handleFormFieldChange}
              autoComplete="username"
              sx={{ bgcolor: 'surface.input', borderRadius: layoutCardRadius }}
            />
          </FormProfile>

          {/* Telefone do usuário */}
          <FormProfile label="Telefone para contato">
            <TextField
              //label="Nome"
              variant="outlined"
              //value={pet.name || ''}
              name="phone"
              id="profile-phone"
              type="tel"
              placeholder="(00) 00000-0000"
              fullWidth
              //onChange={handleFormFieldChange}
              autoComplete="section-profile tel"
              inputProps={{ inputMode: 'tel' }}
              sx={{ bgcolor: 'surface.input', borderRadius: layoutCardRadius }}
            />
          </FormProfile>
        </Box>

        {/* Mudar senha */}
        <Typography variant="storeName" sx={{ lineHeight: '20px' }}>Mudar senha</Typography>

        {/* Campo mudar senha */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, alignSelf: 'stretch' }}>
          {/* Senha */}
          <FieldLogin
            label="Senha"
            type="password"
            value={password}
            onChange={handlePasswordChange}
            placeholder=""
            name="password"
            autoComplete="current-password"
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleSubmit(e);
              }
            }}
            showPasswordToggle={true}
            showPassword={showPassword}
            onTogglePassword={handleTogglePassword}
          />

          {/* Confirmação Senha */}
          <TextField
            name="confirm-username"
            type="email"
            autoComplete="username"
            tabIndex={-1}
            aria-hidden="true"
            value=""
            onChange={() => {}}
            sx={{
              position: 'absolute',
              opacity: 0,
              pointerEvents: 'none',
              width: 1,
              height: 1,
              overflow: 'hidden',
            }}
          />
          <FieldLogin
            label="Confirmar senha"
            type="password"
            value={confirmPassword}
            onChange={handleConfirmPasswordChange}
            placeholder=""
            name="confirmPassword"
            autoComplete="new-password"
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleSubmit(e);
              }
            }}
            showPasswordToggle={true}
            showPassword={showConfirmPassword}
            onTogglePassword={handleToggleConfirmPassword}
          />

        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '10px', alignSelf: 'stretch' }}>
        <ButtonBase onClick={handleSubmit} sx={{ ...buttonStyle, bgcolor: '#F8963E'}}>
          Salvar
        </ButtonBase>
        <ButtonBase onClick={handleCancel} sx={{ ...buttonStyle, bgcolor: '#35454D' }}>
          Cancelar
        </ButtonBase>
      </Box>
    </Box>
  );
};

export default EditProfile;
